import { animationFrameAt, animationPeriod } from "../assets/animation.ts";
import { frameId, type AssetManifest } from "../assets/manifest.ts";
import type { PropBehaviour, TileRef } from "../document/types.ts";
import type { ObjectTypeDefinition } from "./objects.ts";

/**
 * Strips as the editor shows them (ADR-041, ADR-058 amendment of 2026-09-24): the strip a frame starts and
 * how it plays, what a palette frame's badge says about it, the frame a preview of it shows at each moment,
 * and the hint a placed prop resting on a once strip carries. The prop palette and the transition chooser
 * share all of it. A strip is asset data: nothing here names a pack or a behaviour.
 */

/**
 * How a strip plays (ADR-058): `loop` over and over wherever its first frame is drawn, `once` only when a
 * behaviour's transition plays it, resting on its first frame otherwise.
 */
export type StripPlays = "loop" | "once";

/**
 * A strip a frame starts: that frame, the frames it shows in order, how long each shows in milliseconds,
 * and how it plays. The frames and durations are the manifest's own arrays, so they keep their identity
 * across lookups.
 */
export interface AnimationStrip {
  readonly frame: TileRef;
  readonly frames: readonly string[];
  readonly durations: readonly number[];
  readonly plays: StripPlays;
}

/**
 * What a preview needs of a strip.
 */
export type PreviewStrip = Pick<AnimationStrip, "frames" | "durations" | "plays">;

/**
 * What a palette frame's badge says about the strip it starts: how it plays and how many frames it has,
 * as short visible text (`loop 16`, `once 9`) and as a phrase for its label (`Loops, 16 frames`).
 */
export interface StripBadge {
  readonly plays: StripPlays;
  readonly frames: number;
  readonly text: string;
  readonly label: string;
}

/**
 * The frame a preview shows at one moment, whether it is still playing, and how many milliseconds until
 * either changes; `Infinity` once a once strip has returned to its first frame, and never for a loop.
 */
export interface PreviewFrame {
  readonly frame: string;
  readonly playing: boolean;
  readonly nextMs: number;
}

/**
 * How long a preview of a once strip holds the frame it ends on before it returns to its first, in
 * milliseconds: long enough to read the resting drawing, short enough that the strip is soon ready to play
 * again.
 */
export const PREVIEW_HOLD_MS = 600;

/**
 * The strip a frame starts, looping or once, from the editor's asset manifest. A frame inside a strip
 * starts none, since the palette and a placed prop only ever show a strip by its first frame, and neither
 * does a still frame or one the manifest lacks. The transition chooser's `stripStartingAt` is this lookup
 * kept to once strips.
 *
 * @param manifest The editor's asset manifest.
 * @param frame The frame, in its tileset's default variant.
 * @returns The strip, or undefined when the frame starts none.
 */
export function stripAt(manifest: AssetManifest, frame: TileRef): AnimationStrip | undefined {
  const animation = manifest.animations[frameId(frame.tileset, null, frame.tile)];
  if (animation === undefined) {
    return undefined;
  }
  return { frame: { tileset: frame.tileset, tile: frame.tile }, frames: animation.frames, durations: animation.durations, plays: animation.once === true ? "once" : "loop" };
}

/**
 * The badge of a palette frame that starts a strip.
 *
 * @param strip The strip's frames and how it plays.
 * @returns What the badge says.
 */
export function stripBadge(strip: Pick<AnimationStrip, "frames" | "plays">): StripBadge {
  const count = strip.frames.length;
  const frames = count === 1 ? "1 frame" : `${count} frames`;
  if (strip.plays === "loop") {
    return { plays: "loop", frames: count, text: `loop ${count}`, label: `Loops, ${frames}` };
  }
  return { plays: "once", frames: count, text: `once ${count}`, label: `Plays once, ${frames}` };
}

/**
 * What a preview of a strip shows a given time after it started. Each frame shows for its own duration. A
 * loop starts again after its last frame and never stops. A once strip shows its frames once, then holds
 * for `PREVIEW_HOLD_MS` the frame it comes to rest on (its last, or `hold`, as the transition chooser holds
 * the frame the target state rests on), then shows its first frame and stops playing. `nextMs` lets one
 * timer sleep until the earliest change of every preview it drives.
 *
 * @param strip The strip's frames, durations and how it plays.
 * @param elapsedMs Milliseconds since the preview started.
 * @param hold The frame a once strip holds after its last, when not its last; a loop ignores it.
 * @returns The frame shown, whether the preview is still playing, and the time until either changes.
 * @throws RangeError when the time is negative or not finite, or when the strip has no frames, a duration
 *   per frame that is not a positive finite number, or a different number of durations and frames.
 */
export function previewFrameAt(strip: PreviewStrip, elapsedMs: number, hold?: string): PreviewFrame {
  if (!Number.isFinite(elapsedMs) || elapsedMs < 0) {
    throw new RangeError(`a preview is shown at a finite time at or after its start, not ${elapsedMs} ms`);
  }
  if (strip.frames.length === 0 || strip.durations.length !== strip.frames.length || !strip.durations.every((duration) => Number.isFinite(duration) && duration > 0)) {
    throw new RangeError(`a strip needs at least one frame and one positive duration per frame, not ${strip.frames.length} frames and durations ${strip.durations.join(", ")}`);
  }
  const period = animationPeriod(strip);
  const first = strip.frames[0] as string;
  if (strip.plays === "once" && elapsedMs >= period) {
    const returns = period + PREVIEW_HOLD_MS;
    if (elapsedMs < returns) {
      return { frame: hold ?? (strip.frames.at(-1) as string), playing: true, nextMs: returns - elapsedMs };
    }
    return { frame: first, playing: false, nextMs: Number.POSITIVE_INFINITY };
  }
  const index = animationFrameAt(strip, elapsedMs);
  let ends = 0;
  for (let i = 0; i <= index; i += 1) {
    ends += strip.durations[i] as number;
  }
  return { frame: strip.frames[index] as string, playing: true, nextMs: ends - (elapsedMs % period) };
}

/**
 * Whether a type's transitions play a strip: one of them changes state and names the animation it plays,
 * the transitions the chooser offers a strip for (a self-transition carries no animation, P1.8-T2 note 2).
 *
 * @param type The object type.
 * @returns True when a transition of the type plays a strip.
 */
function playsAStrip(type: ObjectTypeDefinition): boolean {
  return type.transitions.some((transition) => transition.from !== transition.to && transition.plays !== undefined);
}

/**
 * The labels of the behaviours a game registers whose transitions play a strip, in registration order: the
 * behaviours that can play a once strip a placed prop rests on.
 *
 * @param types The registered object types.
 * @returns Their labels.
 */
export function playingBehaviours(types: readonly ObjectTypeDefinition[]): string[] {
  return types.filter(playsAStrip).map((type) => type.label);
}

/**
 * Names in a sentence: `A`, `A or B`, `A, B or C`.
 *
 * @param names At least one name.
 * @returns The list.
 */
function eitherOf(names: readonly string[]): string {
  return names.length === 1 ? (names[0] as string) : `${names.slice(0, -1).join(", ")} or ${names.at(-1) as string}`;
}

/**
 * The prop inspector's hint for a placed prop with no behaviour whose frame starts a once strip: since
 * P1.8-T2 such a prop rests on that frame, and only a behaviour's transition plays the strip, so the hint
 * says so and names the behaviours that can. Any other prop gets none.
 *
 * @param manifest The editor's asset manifest.
 * @param types The registered object types.
 * @param prop The placed prop's frame and behaviour.
 * @param prop.frame The frame it shows.
 * @param prop.behaviour The behaviour it carries, if any.
 * @returns The hint, or undefined.
 */
export function restingStripHint(manifest: AssetManifest, types: readonly ObjectTypeDefinition[], prop: { readonly frame: TileRef; readonly behaviour?: PropBehaviour }): string | undefined {
  if (prop.behaviour !== undefined || stripAt(manifest, prop.frame)?.plays !== "once") {
    return undefined;
  }
  const names = playingBehaviours(types);
  if (names.length === 0) {
    return "Plays once, so it rests on its first frame: no behaviour this game registers plays a strip.";
  }
  return `Plays once, so it rests on its first frame until a behaviour plays it: ${eitherOf(names)}.`;
}
